import { useAtomValue } from "jotai"
import { useTranslation } from "react-i18next"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import { Icon } from "./icons/Icon"
import clsx from "clsx"
import { ColorTag, ColorTagValues } from "@/utils/exif"
import { useState } from "react"
import { ratePhoto, setColorTagForPhoto, setFlag } from "@/store/photo/store-photos"
import { Select, SelectContent, SelectItem, SelectGroup } from "@/components/ui/Select"
import { SelectTrigger } from "@radix-ui/react-select"

import { Rating } from "@/lib/catalog"
import { colorTagKeys } from "@/i18n/dual-role-strings"

const Flag = (props: {
	name: "flagged" | "rejected"
	currentFlag: "flagged" | "rejected" | "unflagged"
	valueChanged: (value: "flagged" | "rejected" | "unflagged") => void
}) => {
	const { t } = useTranslation("components")
	const [isHovering, setIsHovering] = useState<boolean>(false)

	return (
		<Icon
			key={props.name}
			className={clsx(
				props.currentFlag === props.name
					? isHovering
						? "fill-base-300"
						: "fill-base-200"
					: isHovering
						? "fill-base-400"
						: "fill-base-500",
			)}
			role="button"
			aria-label={t("photoRatingView.ariaSetPhotoFlag", { name: props.name.toWellFormed() })}
			name={props.name}
			size={16}
			onClick={() => {
				props.valueChanged(props.currentFlag === props.name ? "unflagged" : props.name)
			}}
			onPointerOver={() => setIsHovering(true)}
			onPointerOut={() => setIsHovering(false)}
		/>
	)
}

const RatingView = (props: {
	count: number
	size: number
	showAllStars?: boolean
	onRate?: (rating: Rating) => void
}) => {
	const { t } = useTranslation("components")
	const [hoverIndex, setHoverIndex] = useState<number | null>(null)

	return (
		<div className="flex flex-row space-x-0" data-testid={`${props.count}_stars`}>
			{Array(props.showAllStars ? 5 : props.count)
				.fill(0)
				.map((_, index) => (
					<Icon
						key={index}
						className={clsx(
							index < props.count
								? index === hoverIndex
									? "fill-base-300"
									: "fill-base-200"
								: index === hoverIndex
									? "fill-base-400"
									: "fill-base-500",
						)}
						name={"star_fill"}
						size={props.size}
						role="button"
						aria-label={t("photoRatingView.ariaSetPhotoRating", { rating: index + 1 })}
						onPointerOver={() => setHoverIndex(index)}
						onPointerOut={() => setHoverIndex(null)}
						onClick={() => {
							if (index + 1 === props.count) {
								props.onRate?.(0)
							} else {
								props.onRate?.((index + 1) as Rating)
							}
						}}
					></Icon>
				))}
		</div>
	)
}

const Tag = (props: { color: ColorTag; valueChanged: (value: ColorTag) => void }) => {
	const { t } = useTranslation("components")
	const [isHovering, setIsHovering] = useState<boolean>(false)

	const displayColor = (color: ColorTag) => {
		switch (color) {
			case "Red":
				return "bg-red-500"
			case "Yellow":
				return "bg-yellow-500"
			case "Green":
				return "bg-green-500"
			case "Blue":
				return "bg-blue-500"
			case "Purple":
				return "bg-purple-500"
			default:
				return "bg-base-500"
		}
	}

	return (
		<div className="flex flex-row justify-center">
			<Select
				trackingLabel="thumbnail_color_setter"
				value={props.color}
				onValueChange={(value: ColorTag) => {
					props.valueChanged(value)
				}}
			>
				<SelectTrigger
					aria-label={t("photoRatingView.ariaSetPhotoColorTag")}
					onKeyDown={(e) => {
						// Radix Select uses ArrowDown/Up to open the menu, disabling it to avoid conflicts with enter/quit Cluster
						// Prevent color tag letters from triggering Select's typeahead (R->Red, Y->Yellow, G->Green, B->Blue, P->Purple)
						if (
							e.key === "ArrowDown" ||
							e.key === "ArrowUp" ||
							e.key === "Enter" ||
							/^[rygbp]$/i.test(e.key)
						) {
							e.preventDefault()
						}
					}}
				>
					<div
						className={clsx(
							displayColor(props.color),
							!isHovering && "opacity-80",
							"size-4 rounded-full",
						)}
						onPointerOver={() => setIsHovering(true)}
						onPointerOut={() => setIsHovering(false)}
					></div>
				</SelectTrigger>
				<SelectContent
					aria-label={t("photoRatingView.ariaPhotoColorTagList")}
					side="top"
					align="center"
				>
					<SelectGroup>
						{ColorTagValues.map((color) => (
							<SelectItem key={color} value={color}>
								<div className="flex flex-row items-center space-x-1">
									<div
										className={clsx(displayColor(color), "size-3 rounded-full")}
									></div>
									<div className="text-xs">{t(colorTagKeys[color])}</div>
								</div>
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	)
}

export const PhotoRatingView = () => {
	const { t } = useTranslation("components")
	const currentPhoto = useAtomValue(currentPhotoAtom)

	const renderFlags = () => {
		const photoFlag = currentPhoto?.flag ?? "unflagged"

		const handleFlagChanged = (newFlag: "flagged" | "rejected" | "unflagged") => {
			if (currentPhoto) {
				setFlag([currentPhoto.id], newFlag)
			}
		}

		return (
			<div className="flex flex-row justify-center space-x-2">
				<Flag name="flagged" currentFlag={photoFlag} valueChanged={handleFlagChanged} />
				<Flag name="rejected" currentFlag={photoFlag} valueChanged={handleFlagChanged} />
			</div>
		)
	}

	const renderColorTag = () => {
		const color = currentPhoto?.colorTag ?? "No Color" // i18n-ignore: ColorTag enum value
		return (
			<Tag
				color={color}
				valueChanged={(value) => {
					if (currentPhoto) {
						setColorTagForPhoto(currentPhoto, value)
					}
				}}
			/>
		)
	}

	const renderDivider = () => {
		return <div className="m-0 h-6 w-px bg-base-600"></div>
	}

	return (
		<div
			role="group"
			aria-label={t("photoRatingView.ariaPhotoRatingBar")}
			className="absolute bottom-2 right-2 z-10 w-fit rounded-full bg-base-900 px-3 py-1 data-[state=closed]:animate-floating-tooltip-out data-[state=open]:animate-floating-tooltip-in"
			data-testid="photo-rating-view"
		>
			<div className="flex flex-row items-center justify-center space-x-2">
				{renderFlags()}
				{renderDivider()}
				<RatingView
					count={currentPhoto?.rating ?? 0}
					size={16}
					showAllStars={true}
					onRate={(rating) => {
						if (currentPhoto) {
							ratePhoto(rating, currentPhoto)
						}
					}}
				/>
				{renderDivider()}
				{renderColorTag()}
			</div>
		</div>
	)
}
